import { Language, type RatingDto } from '@posilka/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@posilka/client/api/client';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMe, useUpdateMe } from '@posilka/client/api/me';
import { Avatar } from '@/components/Avatar';
import { Icon } from '@/components/Icon';
import { LinkRow, Row, Section } from '@/components/Section';
import { Segmented } from '@/components/Segmented';
import { Skeleton } from '@/components/Skeleton';
import { SavedList } from '@/features/saved/SavedBar';
import { haptic } from '@/lib/telegram';
import { useAppStore, type AppMode } from '@/lib/store';

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { mode, setMode } = useAppStore();
  const { data: me } = useMe();
  const updateMe = useUpdateMe();
  const rating = useQuery({
    queryKey: ['rating', me?.id],
    queryFn: () => api<RatingDto>(`/users/${me!.id}/rating`),
    enabled: !!me,
  });

  if (!me) return <Skeleton className="h-64" />;

  const changeLanguage = (language: Language) => {
    const previous = i18n.language;
    void i18n.changeLanguage(language);
    updateMe.mutate(
      { language },
      {
        onSuccess: () => haptic.success(),
        onError: () => {
          haptic.error();
          void i18n.changeLanguage(previous);
        },
      },
    );
  };

  return (
    <>
      <div className="card glow mb-4 animate-fade-up p-5">
        <div className="flex items-center gap-4">
          <Avatar firstName={me.firstName} lastName={me.lastName} className="h-16 w-16 text-xl" />
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-xl font-bold tracking-tight">
              {[me.firstName, me.lastName].filter(Boolean).join(' ')}
            </h1>
            {me.username && <p className="truncate text-sm text-muted">@{me.username}</p>}
            <p className="mt-0.5 text-sm">
              {me.phone ?? <span className="text-muted">{t('profile.noPhone')}</span>}
              {me.isAdmin && (
                <span className="ml-2 rounded-full bg-amber/15 px-2 py-0.5 text-xs font-bold text-amber">
                  {t('profile.admin')}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-surface-2 p-3.5">
            <p className="font-display text-2xl font-bold">
              {rating.data?.average ? `★ ${rating.data.average}` : '—'}
            </p>
            <p className="text-xs text-muted">
              {rating.data?.average
                ? `${t('profile.rating')} · ${rating.data.count}`
                : t('profile.noRating')}
            </p>
          </div>
          <div className="rounded-2xl bg-surface-2 p-3.5">
            <p className="font-display text-2xl font-bold">{rating.data?.completedOrders ?? '—'}</p>
            <p className="text-xs text-muted">{t('profile.completed')}</p>
          </div>
        </div>
      </div>

      <Section title={t('mode.switch')}>
        <div className="p-2">
          <Segmented<AppMode>
            value={mode}
            onChange={(m) => {
              haptic.tap();
              setMode(m);
            }}
            options={[
              { value: 'sender', label: t('mode.sender') },
              { value: 'driver', label: t('mode.driver') },
            ]}
          />
        </div>
      </Section>

      <Section title={t('profile.driverStatus')}>
        {me.driverProfile ? (
          <Row
            label={t(`driver.status${me.driverProfile.status}`)}
            value={`${me.driverProfile.carModel} · ${me.driverProfile.carPlate}`}
          />
        ) : (
          <LinkRow to="/driver/apply" icon="🚕">
            {t('profile.applyDriver')}
          </LinkRow>
        )}
        {me.driverProfile?.status === 'REJECTED' && (
          <Link to="/driver/apply" className="block px-5 pb-4 text-sm font-semibold text-link">
            {t('driver.reapply')} →
          </Link>
        )}
      </Section>

      <SavedList />

      <Section title={t('profile.language')}>
        <div className="p-2">
          <Segmented<Language>
            value={i18n.language as Language}
            onChange={changeLanguage}
            options={[
              { value: Language.UZ, label: "O'zbekcha" },
              { value: Language.RU, label: 'Русский' },
            ]}
          />
        </div>
      </Section>

      <Section>
        <LinkRow to="/feedback" icon="💬">
          {t('profile.feedback')}
        </LinkRow>
        <LinkRow to="/rules" icon={<Icon name="info" className="h-5 w-5 text-muted" />}>
          {t('profile.rules')}
        </LinkRow>
      </Section>
    </>
  );
}
